import * as THREE from 'three';
import gsap from 'gsap';

const shadow_image_url = `${window.theme_assets_url}/images/global-opportunity/postcards/postcard-shadow.png`;
export class Postcard {
    constructor(cityData, textureLoader) {
        this.cityData = cityData;

        const width = 5;
        const height = width * (15 / 12);
        const geometry = new THREE.PlaneGeometry(width, height);

        // Load postcard texture and create mesh
        const texture = textureLoader.load(cityData.postCardUrl);
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.generateMipmaps = false;
        texture.minFilter = THREE.LinearFilter;
        texture.maxFilter = THREE.LinearFilter;

        const material = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            side: THREE.DoubleSide,
            opacity: 0,
        });

        this.mesh = new THREE.Mesh(geometry, material);

        // Create shadow mesh
        const shadowGeometry = new THREE.PlaneGeometry(width * 1.1, height * 1.1);
        const shadowTexture = textureLoader.load(shadow_image_url);
        shadowTexture.colorSpace = THREE.SRGBColorSpace;
        const shadowMaterial = new THREE.MeshBasicMaterial({
            map: shadowTexture,
            transparent: true,
            opacity: 0,
            depthWrite: false
        });

        this.shadowMesh = new THREE.Mesh(shadowGeometry, shadowMaterial);
        this.shadowMesh.position.z = -0.01;

        // Create country text canvas
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 512;
        canvas.height = 128;
        context.fillStyle = '#000000';
        context.font = 'bold 70px Arial';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(cityData.country.toUpperCase(), canvas.width / 2, canvas.height / 2);

        // Create country text texture and mesh
        const countryTexture = new THREE.CanvasTexture(canvas);
        const countryGeometry = new THREE.PlaneGeometry(width * 1.2, width * 0.3);
        const countryMaterial = new THREE.MeshBasicMaterial({
            map: countryTexture,
            color: 0x303030,
            transparent: true,
            opacity: 0,
        });

        this.countryMesh = new THREE.Mesh(countryGeometry, countryMaterial);
        this.countryMesh.position.y = 0;
        this.countryMesh.position.z = -0.01;
        const labelPosition = cityData.labelPosition === 'top' ? 1 : -1;
        this.countryMesh.userData.positionY = labelPosition * (height / 1.25);

        // Create collision mesh
        const collisionGeometry = new THREE.PlaneGeometry(width, height);
        const collisionMaterial = new THREE.MeshBasicMaterial({
            transparent: true,
            opacity: 0,
            side: THREE.DoubleSide,
        });

        this.collisionMesh = new THREE.Mesh(collisionGeometry, collisionMaterial);
        this.collisionMesh.renderOrder = 0;
        this.collisionMesh.layers.set(0);
        this.collisionMesh.userData.parentPostcard = this;
        this.collisionMesh.name = cityData.country.toLowerCase();

        // Create visual group for card, shadow, and country text
        this.visualGroup = new THREE.Group();
        this.visualGroup.add(this.shadowMesh);
        this.visualGroup.add(this.countryMesh);
        this.visualGroup.add(this.mesh);

        // Create main group that contains everything
        this.group = new THREE.Group();
        this.group.add(this.visualGroup);
        this.group.add(this.collisionMesh);

        // Set rotation from cityData
        this.group.rotation.z = THREE.MathUtils.degToRad(parseFloat(cityData.postCardRotate));

        // Store original values for animations
        this.initialAnimationScale = 3;
        this.originalScale = 1;
        this.hoverScale = 1.3;
        this.duration = 0.6;
        this.appearanceDuration = 1.4;
        this.appearanceDelay = 0.02;

        // Shadow opacity values
        this.originalShadowOpacity = 0.2;
        this.hoverShadowOpacity = 0.3;

        // Store original z position for hover effect
        this.originalZ = (cityData.postCardOrder + 1) * 0.01;
        this.hoverZ = 1;

        // Set initial z-positions
        this.group.position.z = this.originalZ;
        this.visualGroup.position.z = this.originalZ;

        // Add reference to cityData in userData
        this.mesh.userData.cityData = cityData;
        this.shadowMesh.userData.cityData = cityData;
    }

    hover() {
        // Scale up both card and shadow
        gsap.to(this.mesh.scale, {
            x: this.hoverScale,
            y: this.hoverScale,
            z: this.hoverScale,
            duration: this.duration,
            ease: "power2.out"
        });

        gsap.to(this.shadowMesh.scale, {
            x: this.hoverScale * 1.05,
            y: this.hoverScale * 1.05,
            z: this.hoverScale * 1.05,
            duration: this.duration,
            ease: "power2.out"
        });

        gsap.to(this.shadowMesh.position, {
            y: -0.25,
            duration: this.duration,
            ease: "power2.out"
        });

        // Move up only the visual group in Z space
        gsap.to(this.visualGroup.position, {
            z: this.hoverZ,
            duration: this.duration,
            ease: "power2.out"
        });

        // Increase shadow opacity
        gsap.to(this.shadowMesh.material, {
            opacity: this.hoverShadowOpacity,
            duration: this.duration,
            ease: "power2.out"
        });

        // Show country text
        gsap.to(this.countryMesh.material, {
            opacity: 1,
            duration: this.duration,
            ease: "power2.out"
        });

        gsap.to(this.countryMesh.position, {
            y: this.countryMesh.userData.positionY,
            duration: this.duration,
            ease: "power2.out"
        });
    }

    unhover() {
        // Scale down both card and shadow
        gsap.to(this.mesh.scale, {
            x: this.originalScale,
            y: this.originalScale,
            z: this.originalScale,
            duration: this.duration,
            ease: "power2.out"
        });

        gsap.to(this.shadowMesh.scale, {
            x: this.originalScale,
            y: this.originalScale,
            z: this.originalScale,
            duration: this.duration,
            ease: "power2.out"
        });

        gsap.to(this.shadowMesh.position, {
            y: 0,
            duration: this.duration,
            ease: "power2.out"
        });

        // Move back visual group to original Z position
        gsap.to(this.visualGroup.position, {
            z: 0,
            duration: this.duration,
            ease: "power2.out"
        });

        // Decrease shadow opacity
        gsap.to(this.shadowMesh.material, {
            opacity: this.originalShadowOpacity,
            duration: this.duration,
            ease: "power2.out"
        });

        // Hide country text
        gsap.to(this.countryMesh.material, {
            opacity: 0,
            duration: this.duration,
            ease: "power2.out"
        });

        gsap.to(this.countryMesh.position, {
            y: 0,
            duration: this.duration,
            ease: "power2.out"
        });
    }

    appear(delay, isSelected) {
        // Set initial scale for both meshes
        this.mesh.scale.set(
            this.initialAnimationScale,
            this.initialAnimationScale,
            this.initialAnimationScale
        );
        this.shadowMesh.scale.set(
            this.initialAnimationScale * 1.1,
            this.initialAnimationScale * 1.1,
            this.initialAnimationScale
        );

        // Start with some Y offset
        const originalY = this.group.position.y;
        this.group.position.y += 10;

        // Custom bounce configuration with 2 bounces
        const customBounce = {
            strength: 0.6,
            squash: 0,
            squashID: "customBounce"
        };

        const scale = isSelected ? this.hoverScale : this.originalScale;

        // Make visible and animate right after
        gsap.timeline({
            delay: delay
        })
        .set([this.mesh.material, this.shadowMesh.material], {
            opacity: (index) => index === 0 ? 1 : this.originalShadowOpacity
        })
        .to([this.mesh.scale, this.shadowMesh.scale], {
            x: (index) => index === 0 ? scale : scale,
            y: (index) => index === 0 ? scale : scale,
            z: scale,
            duration: this.appearanceDuration,
            ease: `bounce.out(${customBounce.strength}, 0)`
        }, 0)
        .to(this.group.position, {
            y: originalY,
            duration: this.appearanceDuration,
            ease: `bounce.out(${customBounce.strength}, 0)`
        }, 0);
    }

    setPosition(x, y) {
        this.group.position.x = x;
        this.group.position.y = y;
    }
}
